// English strings. This object is the source of truth for the key set: every
// other locale is typed as `Record<TranslationKey, string>`, so a missing or
// misspelled key in a translation is a compile error rather than a blank label.
//
// Interpolation uses {name} placeholders — see `translate()` in ./index.ts.

export const en = {
  // ── app / navigation ──────────────────────────────────────────────────────
  'app.name': 'ARES Invoice',
  'app.tagline': 'Invoicing, clients and products',
  'nav.invoices': 'Invoices',
  'nav.clients': 'Clients',
  'nav.products': 'Products',
  'nav.users': 'Users',
  'nav.settings': 'Settings',
  'nav.signOut': 'Sign out',
  'nav.language': 'Language',
  'nav.menu': 'Menu',

  // ── common ────────────────────────────────────────────────────────────────
  'common.save': 'Save',
  'common.saveChanges': 'Save changes',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.new': 'New',
  'common.search': 'Search',
  'common.searchPlaceholder': 'Search…',
  'common.clear': 'Clear',
  'common.loading': 'Loading…',
  'common.saving': 'Saving…',
  'common.noResults': 'Nothing found',
  'common.noResultsHint': 'Try a different search term.',
  'common.required': 'Required',
  'common.optional': 'optional',
  'common.active': 'Active',
  'common.inactive': 'Inactive',
  'common.status': 'Status',
  'common.actions': 'Actions',
  'common.created': 'Created',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.retry': 'Try again',
  'common.saved': 'Saved',
  'common.deleted': 'Deleted',
  'common.of': 'of',
  'common.page': 'Page {page} of {total}',
  'common.showing': 'Showing {from}–{to} of {total}',
  'common.previous': 'Previous',
  'common.next': 'Next',
  'common.all': 'All',
  'common.total': 'Total',
  'common.unsavedChanges': 'You have unsaved changes. Leave anyway?',

  // ── sign in ───────────────────────────────────────────────────────────────
  'login.title': 'Sign in',
  'login.subtitle': 'Sign in to continue',
  'login.username': 'Username or email',
  'login.password': 'Password',
  'login.submit': 'Sign in',
  'login.signingIn': 'Signing in…',
  'login.signedOut': 'You have been signed out.',

  // ── invoices ──────────────────────────────────────────────────────────────
  'invoices.title': 'Invoices',
  'invoices.new': 'New invoice',
  'invoices.searchPlaceholder': 'Client name or invoice number',
  'invoices.number': 'No.',
  'invoices.date': 'Date',
  'invoices.client': 'Client',
  'invoices.seller': 'Issued by',
  'invoices.lines': 'Lines',
  'invoices.empty': 'No invoices yet',
  'invoices.emptyHint': 'Create your first invoice to get started.',
  'invoices.download': 'Download PDF',
  'invoices.deleteConfirm': 'Delete invoice #{number}? This cannot be undone.',
  'invoices.deleted': 'Invoice #{number} deleted',
  'invoices.markPaid': 'Mark as paid',
  'invoices.markPending': 'Mark as pending',
  'invoices.markedPaid': 'Invoice #{number} marked as paid',
  'invoices.markedPending': 'Invoice #{number} marked as pending',
  'invoices.filterStatus': 'Status',
  'invoices.totals.invoices': 'Invoices',
  'invoices.totals.net': 'Net',
  'invoices.totals.tax': 'Tax',
  'invoices.totals.gross': 'Gross',
  'invoices.totals.outstanding': 'Outstanding',

  // ── invoice editor ────────────────────────────────────────────────────────
  'invoice.new.title': 'New invoice',
  'invoice.edit.title': 'Invoice #{number}',
  'invoice.field.client': 'Client',
  'invoice.field.clientPlaceholder': 'Type to search clients…',
  'invoice.field.phone': 'Phone',
  'invoice.field.email': 'Email',
  'invoice.field.seller': 'Issued by',
  'invoice.field.date': 'Date',
  'invoice.field.paymentMethod': 'Payment',
  'invoice.field.status': 'Status',
  'invoice.field.notes': 'Notes',
  'invoice.field.notesPlaceholder': 'Shown on the document, below the totals',
  'invoice.selectClient': 'Select a client first',
  'invoice.addLine': 'Add product',
  'invoice.addLines': 'Add products',
  'invoice.newClient': 'New client',
  'invoice.newProduct': 'New product',
  'invoice.lines.code': 'Code',
  'invoice.lines.quantity': 'Qty',
  'invoice.lines.description': 'Description',
  'invoice.lines.unitPrice': 'Unit price',
  'invoice.lines.lineTotal': 'Line total',
  'invoice.lines.remove': 'Remove line',
  'invoice.lines.empty': 'No products added yet',
  'invoice.lines.emptyHint': 'Use “Add products” to build the document.',
  'invoice.subtotal': 'Subtotal',
  'invoice.tax': 'Tax ({rate}%)',
  'invoice.total': 'Total',
  'invoice.save': 'Save invoice',
  'invoice.created': 'Invoice #{number} created',
  'invoice.updated': 'Invoice #{number} updated',
  'invoice.taxRateNote': 'The tax rate is taken from Settings when the document is created, then fixed.',
  'invoice.picker.title': 'Add products',
  'invoice.picker.add': 'Add',
  'invoice.picker.added': '{name} added',
  'invoice.picker.searchPlaceholder': 'Search by code or name',

  // ── payment methods / statuses ────────────────────────────────────────────
  'payment.cash': 'Cash',
  'payment.check': 'Cheque',
  'payment.transfer': 'Bank transfer',
  'payment.credit': 'Credit',
  'status.paid': 'Paid',
  'status.pending': 'Pending',

  // ── clients ───────────────────────────────────────────────────────────────
  'clients.title': 'Clients',
  'clients.new': 'New client',
  'clients.edit': 'Edit client',
  'clients.searchPlaceholder': 'Name, tax ID, phone or email',
  'clients.field.name': 'Name',
  'clients.field.taxId': 'Tax ID',
  'clients.field.phone': 'Phone',
  'clients.field.email': 'Email',
  'clients.field.address': 'Address',
  'clients.invoices': 'Invoices',
  'clients.empty': 'No clients yet',
  'clients.emptyHint': 'Add a client to start invoicing.',
  'clients.deleteConfirm': 'Delete {name}?',
  'clients.deleteBlocked': '{name} has {count} invoice(s) and cannot be deleted. Mark it inactive instead.',
  'clients.created': 'Client created',
  'clients.updated': 'Client updated',

  // ── products ──────────────────────────────────────────────────────────────
  'products.title': 'Products',
  'products.new': 'New product',
  'products.edit': 'Edit product',
  'products.searchPlaceholder': 'Code or name',
  'products.field.code': 'Code',
  'products.field.name': 'Description',
  'products.field.unitPrice': 'Unit price',
  'products.empty': 'No products yet',
  'products.emptyHint': 'Add a product to put on invoices.',
  'products.deleteConfirm': 'Delete {name}?',
  'products.deleteKeepsHistory': 'Invoices already issued keep their copy of this product.',
  'products.created': 'Product created',
  'products.updated': 'Product updated',

  // ── users ─────────────────────────────────────────────────────────────────
  'users.title': 'Users',
  'users.new': 'New user',
  'users.edit': 'Edit user',
  'users.searchPlaceholder': 'Name, username or email',
  'users.field.firstName': 'First name',
  'users.field.lastName': 'Last name',
  'users.field.username': 'Username',
  'users.field.email': 'Email',
  'users.field.password': 'Password',
  'users.field.passwordRepeat': 'Repeat password',
  'users.field.locale': 'Language',
  'users.changePassword': 'Change password',
  'users.passwordChanged': 'Password changed',
  'users.passwordHint': 'At least 8 characters.',
  'users.deleteConfirm': 'Delete {name}?',
  'users.deleteBlocked': '{name} has issued {count} invoice(s) and cannot be deleted. Mark them inactive instead.',
  'users.deleteSelf': 'You cannot delete the account you are signed in with.',
  'users.created': 'User created',
  'users.updated': 'User updated',
  'users.you': 'you',

  // ── settings ──────────────────────────────────────────────────────────────
  'settings.title': 'Settings',
  'settings.company': 'Company',
  'settings.invoicing': 'Invoicing',
  'settings.appearance': 'Appearance',
  'settings.field.companyName': 'Company name',
  'settings.field.taxId': 'Tax ID',
  'settings.field.address': 'Address',
  'settings.field.city': 'City',
  'settings.field.region': 'Region / province',
  'settings.field.postalCode': 'Postal code',
  'settings.field.phone': 'Phone',
  'settings.field.email': 'Email',
  'settings.field.taxRate': 'Tax rate (%)',
  'settings.field.currency': 'Currency',
  'settings.field.defaultLocale': 'Default language',
  'settings.logo': 'Logo',
  'settings.logoUpload': 'Upload logo',
  'settings.logoHint': 'PNG or JPEG, up to 2 MB. Appears on every invoice PDF.',
  'settings.logoRemove': 'Remove logo',
  'settings.taxRateHint': 'Applies to invoices created from now on. Existing invoices keep the rate they were issued with.',
  'settings.saved': 'Settings saved',

  // ── PDF ───────────────────────────────────────────────────────────────────
  'pdf.invoice': 'INVOICE',
  'pdf.invoiceNumber': 'Invoice no.',
  'pdf.date': 'DATE',
  'pdf.paymentMethod': 'PAYMENT METHOD',
  'pdf.billTo': 'BILL TO',
  'pdf.taxId': 'Tax ID',
  'pdf.phone': 'Phone',
  'pdf.email': 'Email',
  'pdf.quantity': 'QTY',
  'pdf.description': 'DESCRIPTION',
  'pdf.unitPrice': 'UNIT PRICE',
  'pdf.lineTotal': 'TOTAL',
  'pdf.subtotal': 'SUBTOTAL',
  'pdf.tax': 'TAX ({rate}%)',
  'pdf.total': 'TOTAL',
  'pdf.page': 'Page {page} of {total}',
  'pdf.issuedBy': 'Issued by',
  'pdf.notes': 'Notes',

  // ── quotations ────────────────────────────────────────────────────────────
  'nav.quotations': 'Quotations',
  'quotations.title': 'Quotations',
  'quotations.new': 'New quotation',
  'quotations.searchPlaceholder': 'Client name or quotation number',
  'quotations.number': 'No.',
  'quotations.validUntil': 'Valid until',
  'quotations.empty': 'No quotations yet',
  'quotations.emptyHint': 'Quote a client before invoicing them.',
  'quotations.deleteConfirm': 'Delete quotation #{number}? This cannot be undone.',
  'quotations.deleted': 'Quotation #{number} deleted',
  'quotations.download': 'Download PDF',
  'quotations.totals.quotations': 'Quotations',
  'quotations.totals.open': 'Open value',
  'quotations.totals.accepted': 'Accepted',
  'quotation.new.title': 'New quotation',
  'quotation.edit.title': 'Quotation #{number}',
  'quotation.save': 'Save quotation',
  'quotation.created': 'Quotation #{number} created',
  'quotation.updated': 'Quotation #{number} updated',
  'quotation.field.validUntil': 'Valid until',
  'quotation.validityHint': 'Shown on the quotation. After this date it is marked expired.',
  'quotation.convert': 'Convert to invoice',
  'quotation.convertConfirm': 'Create an invoice from quotation #{number}?\n\nThe invoice keeps the quoted tax rate and currency, so the client is billed exactly what was quoted.',
  'quotation.converted': 'Invoice #{number} created from the quotation',
  'quotation.convertedBanner': 'Converted to invoice #{number}.',
  'quotation.convertedOpen': 'Open the invoice',
  'quotation.alreadyConverted': 'This quotation has already been converted to an invoice.',
  'quotation.fromQuotation': 'From quotation #{number}',
  'status.draft': 'Draft',
  'status.sent': 'Sent',
  'status.accepted': 'Accepted',
  'status.rejected': 'Rejected',
  'status.expired': 'Expired',

  // ── email ─────────────────────────────────────────────────────────────────
  'email.send': 'Send by email',
  'email.title': 'Send by email',
  'email.to': 'To',
  'email.cc': 'Cc',
  'email.subject': 'Subject',
  'email.message': 'Message',
  'email.attachment': 'Attachment',
  'email.sendButton': 'Send',
  'email.sending': 'Sending…',
  'email.sent': 'Sent to {to}',
  'email.lastSent': 'Emailed {date}',
  'email.noClientAddress': 'This client has no email address. Enter one below.',
  'email.invoiceSubject': 'Invoice {number} — {company}',
  'email.quotationSubject': 'Quotation {number} — {company}',
  'email.invoiceBody': 'Dear {client},\n\nPlease find attached invoice {number} dated {date}, for {total}.\n\n{payment}\n\nKind regards,\n{company}',
  'email.quotationBody': 'Dear {client},\n\nPlease find attached quotation {number}, for {total}. It is valid until {validUntil}.\n\nWe remain at your disposal for any questions.\n\nKind regards,\n{company}',
  'email.bodyPayment': 'Payment can be made to: {account}',
  'email.notConfigured': 'Email is not configured. Fill in the mail server under Settings.',
  'email.failed': 'The message could not be sent. Check the mail server under Settings.',
  'email.invalidRecipient': 'Enter a valid email address.',

  // ── bank details ──────────────────────────────────────────────────────────
  'settings.payment': 'Payment details',
  'settings.field.bankName': 'Bank name',
  'settings.field.bankAccount': 'Bank account / IBAN',
  'settings.bankHint': 'Printed at the foot of every invoice and quotation PDF.',
  'pdf.bankAccount': 'Bank account',
  'pdf.quotation': 'QUOTATION',
  'pdf.quotationNumber': 'Quotation no.',
  'pdf.validUntil': 'VALID UNTIL',

  // ── two-factor authentication ─────────────────────────────────────────────
  'twofactor.title': 'Two-step verification',
  'twofactor.required': 'Two-step verification is required on every account.',
  'twofactor.setUp': 'Set up two-step verification',
  'twofactor.setUpIntro': 'Scan this code with an authenticator app — Google Authenticator, Authy, 1Password or similar — then enter the six-digit code it shows.',
  'twofactor.manualEntry': 'Cannot scan? Enter this key by hand',
  'twofactor.code': 'Verification code',
  'twofactor.codePlaceholder': '000000',
  'twofactor.codeHint': 'Six digits from your authenticator app.',
  'twofactor.confirm': 'Confirm and continue',
  'twofactor.challengeTitle': 'Verification code',
  'twofactor.challengeIntro': 'Enter the code from your authenticator app.',
  'twofactor.useRecovery': 'Use a recovery code instead',
  'twofactor.useAuthenticator': 'Use your authenticator app instead',
  'twofactor.recoveryCode': 'Recovery code',
  'twofactor.recoveryPlaceholder': 'XXXXX-XXXXX',
  'twofactor.recoveryHint': 'One of the codes you saved when you set this up. Each works once.',
  'twofactor.verify': 'Verify',
  'twofactor.verifying': 'Verifying…',
  'twofactor.invalidCode': 'That code is not correct. Check your authenticator and try again.',
  'twofactor.locked': 'Too many incorrect codes. Try again in a few minutes.',
  'twofactor.notStarted': 'Start the setup again — no pending enrolment was found.',
  'twofactor.notEnrolled': 'This account has not finished setting up two-step verification.',
  'twofactor.recoveryTitle': 'Save your recovery codes',
  'twofactor.recoveryIntro': 'Each code works once, and gets you in if you lose your phone. Store them somewhere safe — they are not shown again.',
  'twofactor.recoveryDownload': 'Download',
  'twofactor.recoveryCopy': 'Copy',
  'twofactor.recoveryCopied': 'Copied',
  'twofactor.recoveryAcknowledge': 'I have saved these codes',
  'twofactor.recoveryRemaining': '{count} recovery codes left',
  'twofactor.recoveryLow': 'Only {count} recovery codes left. Generate a new set.',
  'twofactor.recoveryUsed': 'You signed in with a recovery code. {count} left.',
  'twofactor.regenerate': 'Generate new recovery codes',
  'twofactor.regenerated': 'New recovery codes generated. The old ones no longer work.',
  'twofactor.status': 'Status',
  'twofactor.enabledSince': 'Active since {date}',
  'twofactor.reconfigure': 'Set up a new device',
  'twofactor.reconfigureHint': 'Replaces your current authenticator. The old one stops working immediately.',
  'twofactor.confirmPassword': 'Confirm your password to continue',
  'twofactor.adminReset': 'Reset two-step verification',
  'twofactor.adminResetConfirm': 'Reset two-step verification for {name}?\n\nTheir authenticator and recovery codes stop working, and they set it up again the next time they sign in.',
  'twofactor.adminResetDone': 'Two-step verification reset for {name}',
  'twofactor.adminResetHint': 'Use this when someone has lost both their phone and their recovery codes.',
  'twofactor.notConfigured': 'Not set up',
  'twofactor.security': 'Security',
  'twofactor.myAccount': 'My account',

  // ── suppliers and purchase invoices ───────────────────────────────────────
  'nav.purchases': 'Purchases',
  'suppliers.title': 'Suppliers',
  'suppliers.new': 'New supplier',
  'suppliers.edit': 'Edit supplier',
  'suppliers.searchPlaceholder': 'Name, tax ID or email',
  'suppliers.field.name': 'Name',
  'suppliers.field.taxId': 'Tax ID',
  'suppliers.field.phone': 'Phone',
  'suppliers.field.email': 'Email',
  'suppliers.field.address': 'Address',
  'suppliers.field.notes': 'Notes',
  'suppliers.empty': 'No suppliers yet',
  'suppliers.emptyHint': 'Add the providers and manufacturers who invoice you.',
  'suppliers.invoices': 'Invoices',
  'suppliers.billed': 'Billed',
  'suppliers.created': 'Supplier created',
  'suppliers.updated': 'Supplier updated',
  'suppliers.deleteConfirm': 'Delete {name}?',
  'suppliers.deleteBlocked': '{name} has {count} invoice(s) and cannot be deleted. Mark them inactive instead.',
  'purchases.title': 'Purchase invoices',
  'purchases.subtitle': 'Invoices received from suppliers',
  'purchases.new': 'Record an invoice',
  'purchases.edit': 'Edit purchase invoice',
  'purchases.searchPlaceholder': 'Supplier, number or description',
  'purchases.empty': 'No purchase invoices yet',
  'purchases.emptyHint': 'Upload an invoice a supplier has sent you.',
  'purchases.field.supplier': 'Supplier',
  'purchases.field.number': 'Invoice number',
  'purchases.field.numberHint': "The supplier's own reference.",
  'purchases.field.issuedOn': 'Invoice date',
  'purchases.field.receivedOn': 'Received',
  'purchases.field.dueOn': 'Due',
  'purchases.field.description': 'Description',
  'purchases.field.subtotal': 'Net',
  'purchases.field.taxTotal': 'Tax',
  'purchases.field.total': 'Total',
  'purchases.field.status': 'Status',
  'purchases.field.paidOn': 'Paid on',
  'purchases.field.notes': 'Notes',
  'purchases.amountsHint': 'Enter the amounts exactly as they appear on the invoice. The total is the sum.',
  'purchases.file': 'Invoice file',
  'purchases.fileUpload': 'Upload file',
  'purchases.fileReplace': 'Replace file',
  'purchases.fileRemove': 'Remove file',
  'purchases.fileHint': 'PDF, PNG or JPEG, up to 10 MB. This is the original document your accountant receives.',
  'purchases.fileNone': 'No file attached',
  'purchases.fileMissing': 'No file',
  'purchases.download': 'Download the original',
  'purchases.created': 'Purchase invoice recorded',
  'purchases.updated': 'Purchase invoice updated',
  'purchases.deleteConfirm': 'Delete invoice {number} from {supplier}?\n\nThe uploaded file is deleted too.',
  'purchases.deleted': 'Purchase invoice deleted',
  'purchases.totals.invoices': 'Invoices',
  'purchases.totals.net': 'Net',
  'purchases.totals.tax': 'Tax',
  'purchases.totals.gross': 'Gross',
  'purchases.totals.outstanding': 'Unpaid',
  'purchases.numberTaken': 'That supplier already has an invoice with this number.',
  'status.pendingPayment': 'Unpaid',
  'status.paidPurchase': 'Paid',

  // ── accounting export ─────────────────────────────────────────────────────
  'nav.accounting': 'Accounting',
  'accounting.title': 'Send to accounting',
  'accounting.subtitle': 'Select the period, choose the documents, and send them as one archive.',
  'accounting.period': 'Period',
  'accounting.from': 'From',
  'accounting.to': 'To',
  'accounting.quickYear': 'Year {year}',
  'accounting.quickQuarter': 'Q{quarter} {year}',
  'accounting.onlyUnsent': 'Only documents not sent before',
  'accounting.sales': 'Invoices issued to clients',
  'accounting.purchases': 'Invoices received from suppliers',
  'accounting.selectAll': 'Select all',
  'accounting.selectNone': 'Clear selection',
  'accounting.selected': '{count} selected',
  'accounting.nothingSelected': 'Select at least one document.',
  'accounting.noneInPeriod': 'No documents in this period.',
  'accounting.alreadySent': 'Sent {date}',
  'accounting.noFileWarning': '{count} purchase invoice(s) have no file attached. Only their details are included.',
  'accounting.send': 'Send to accounting',
  'accounting.sending': 'Preparing and sending…',
  'accounting.sent': 'Sent {count} document(s) to {to}',
  'accounting.archive': 'Archive',
  'accounting.archiveHint': 'One .zip containing every selected document plus a summary spreadsheet.',
  'accounting.summaryFile': 'summary.csv',
  'accounting.subject': 'Accounting {period} — {company}',
  'accounting.body': 'Hello,\n\nAttached is the documentation for {period}:\n\n· {salesCount} invoices issued, totalling {salesTotal}\n· {purchasesCount} invoices received, totalling {purchasesTotal}\n\nThe archive also contains a summary spreadsheet.\n\nKind regards,\n{company}',
  'accounting.periodLabel': '{from} to {to}',
  'accounting.recipientMissing': 'Set the accountant\u2019s email in Settings, or enter one below.',
  'settings.accounting': 'Accounting',
  'settings.field.accountantName': 'Accountant name',
  'settings.field.accountantEmail': 'Accountant email',
  'settings.accountingHint': 'Where the accounting export is addressed by default.',

  // ── reports ───────────────────────────────────────────────────────────────
  'nav.reports': 'Reports',
  'reports.title': 'Client reports',
  'reports.subtitle': 'What each client has been invoiced, by year or across the whole period.',
  'reports.period': 'Period',
  'reports.allTime': 'Entire period',
  'reports.year': 'Year {year}',
  'reports.client': 'Client',
  'reports.invoices': 'Invoices',
  'reports.net': 'Net',
  'reports.tax': 'Tax',
  'reports.gross': 'Gross',
  'reports.paid': 'Paid',
  'reports.outstanding': 'Outstanding',
  'reports.first': 'First',
  'reports.last': 'Last',
  'reports.share': 'Share',
  'reports.empty': 'No invoices in this period',
  'reports.emptyHint': 'Choose another year, or the entire period.',
  'reports.download': 'Download CSV',
  'reports.totalsRow': 'All clients',
  'reports.clientHistory': '{name} — year by year',
  'reports.backToAll': 'All clients',
  'reports.averageInvoice': 'Average invoice',
  'reports.noYears': 'No invoices recorded.',

  // ── mail server ───────────────────────────────────────────────────────────
  'mail.title': 'Mail server',
  'mail.subtitle': 'The SMTP relay invoices, quotations and the accounting export are sent through.',
  'mail.enabled': 'Send email through this server',
  'mail.disabledNote': 'Sending is off. Invoices and quotations can still be downloaded as PDFs.',
  'mail.connection': 'Connection',
  'mail.host': 'Host',
  'mail.hostHint': 'The relay’s name or address — smtp.example.com, or 127.0.0.1 for a mail server on this machine.',
  'mail.port': 'Port',
  'mail.security': 'Encryption',
  'mail.securityNone': 'None, or upgrade if offered',
  'mail.securityStarttls': 'STARTTLS — required',
  'mail.securityTls': 'TLS from the start (usually port 465)',
  'mail.username': 'Username',
  'mail.usernameHint': 'Leave both blank for a relay that accepts mail without signing in.',
  'mail.password': 'Password',
  'mail.passwordStored': 'Stored. Leave blank to keep it, or type a new one to replace it.',
  'mail.tlsServername': 'Certificate name',
  'mail.tlsServernameHint': 'Only when the relay holds a certificate for a different name than the host above — a mail server on this machine reached at 127.0.0.1. Leave it empty and the certificate is checked against the host.',
  'mail.sender': 'Sender',
  'mail.fromName': 'Sender name',
  'mail.fromEmail': 'Sender address',
  'mail.fromHint': 'The mailbox your relay has authorised: where bounces return and what SPF is checked against. Clients see the company name and email from Company above, not this.',
  'mail.bcc': 'Blind copy every message to',
  'mail.bccHint': 'Optional. Separate several addresses with commas.',
  'mail.test': 'Send a test email',
  'mail.testing': 'Sending…',
  'mail.testTo': 'Send the test to',
  'mail.testOk': 'Test message sent to {to}. Check it arrived.',
  'mail.testFailed': 'The mail server could not be used: {detail}',
  'mail.testNoRecipient': 'Enter an address to send the test to.',
  'mail.notMigrated': 'The mail server cannot be saved until the database upgrade db/10-mail-settings.sql has been applied.',
  'mail.testSubject': 'ARES Invoice test message',
  'mail.testBody': 'This is a test message from ARES Invoice, sent through {host}:{port}.\n\nIf you are reading it, invoices and quotations can be emailed from the application.',
  'mail.fromEnvironment': 'These values come from the SMTP_* variables in .env and are re-read at every restart. Saving this form takes over from them for good.',
  'mail.saveFirst': 'The test uses what is saved, so the form is saved before it runs.',

  // ── backups ───────────────────────────────────────────────────────────────
  'backup.title': 'Backups',
  'backup.subtitle': 'A single archive holding the database and every uploaded file.',
  'backup.enabled': 'Back up automatically',
  'backup.disabledNote': 'Automatic backups are off. You can still run one by hand.',
  'backup.destination': 'Where to keep them',
  'backup.destinationHint': 'Keep both: the local copy restores quickly, the remote one survives losing this server.',
  'backup.noDestination': 'Switch on at least one destination.',
  'backup.localSection': 'On this server',
  'backup.sftpSection': 'On another server (SFTP)',
  'backup.localEnabled': 'Keep a copy on this server',
  'backup.sftpEnabled': 'Keep a copy on another server',
  'backup.localKeepCount': 'Copies to keep here',
  'backup.sftpKeepCount': 'Copies to keep there',
  'backup.bothDestinations': 'local and remote',
  'backup.destinationLocal': 'A directory on this server',
  'backup.destinationSftp': 'Another server over SFTP',
  'backup.frequency': 'How often',
  'backup.daily': 'Every day',
  'backup.weekly': 'Every week',
  'backup.time': 'At',
  'backup.dayOfWeek': 'On',
  'backup.keepCount': 'Archives to keep',
  'backup.keepHint': 'Older archives at the destination are deleted after each run.',
  'backup.includeFiles': 'Include uploaded files (logos, supplier invoices)',
  'backup.includeFilesHint': 'Without these, a restored database cannot show a purchase invoice\u2019s PDF.',
  'backup.localPath': 'Directory',
  'backup.localPathHint': 'An absolute path. It is created if it does not exist.',
  'backup.sftpHost': 'Host',
  'backup.sftpPort': 'Port',
  'backup.sftpUsername': 'Username',
  'backup.sftpPath': 'Remote directory',
  'backup.sftpAuth': 'Authentication',
  'backup.sftpAuthPassword': 'Password',
  'backup.sftpAuthKey': 'Private key',
  'backup.sftpPassword': 'Password',
  'backup.sftpPrivateKey': 'Private key',
  'backup.sftpPrivateKeyHint': 'Paste the key itself, including the BEGIN and END lines.',
  'backup.sftpPassphrase': 'Key passphrase',
  'backup.secretStored': 'Stored. Leave blank to keep it, or type a new one to replace it.',
  'backup.secretCleared': 'Clearing this field removes the stored value.',
  'backup.test': 'Test the destination',
  'backup.testing': 'Testing\u2026',
  'backup.testOk': 'Reachable and writable: {detail}',
  'backup.runNow': 'Back up now',
  'backup.running': 'Backing up\u2026',
  'backup.ranOk': 'Backup finished: {filename} ({size})',
  'backup.ranFailed': 'The backup failed. See the history below.',
  'backup.lastRun': 'Last run',
  'backup.nextRun': 'Next run',
  'backup.never': 'Never',
  'backup.history': 'Recent backups',
  'backup.historyEmpty': 'No backups yet',
  'backup.historyEmptyHint': 'Run one by hand to check the destination works.',
  'backup.started': 'Started',
  'backup.contents': 'Contents',
  'backup.contentsSummary': '{tables} tables, {rows} rows, {files} files',
  'backup.size': 'Size',
  'backup.statusRunning': 'Running',
  'backup.statusSuccess': 'Succeeded',
  'backup.statusFailed': 'Failed',
  'backup.triggerScheduled': 'Scheduled',
  'backup.triggerManual': 'Manual',
  'backup.download': 'Download a backup now',
  'backup.downloadHint': 'Builds an archive and downloads it here, without touching the destination.',
  'backup.localPathRequired': 'Enter the directory to keep backups in.',
  'backup.sftpIncomplete': 'Enter at least the host and username for the remote server.',
  'backup.testFailed': 'Could not reach the destination: {detail}',
  'backup.restoreTitle': 'Restoring',
  'backup.restoreSteps': 'Unzip the archive, load database.sql into an empty database, then copy storage/ over the application\u2019s storage directory.',
  'backup.sunday': 'Sunday',
  'backup.monday': 'Monday',
  'backup.tuesday': 'Tuesday',
  'backup.wednesday': 'Wednesday',
  'backup.thursday': 'Thursday',
  'backup.friday': 'Friday',
  'backup.saturday': 'Saturday',

  // ── errors (server + client) ───────────────────────────────────────────────
  'error.unexpected': 'Something went wrong. Please try again.',
  'error.network': 'Cannot reach the server.',
  'error.unauthorized': 'Please sign in to continue.',
  'error.forbidden': 'You do not have access to that.',
  'error.notFound': 'Not found.',
  'error.invalidCredentials': 'Username or password is incorrect.',
  'error.accountInactive': 'This account has been deactivated.',
  'error.validation': 'Please check the highlighted fields.',
  'error.required': 'This field is required.',
  'error.invalidEmail': 'Enter a valid email address.',
  'error.passwordTooShort': 'Password must be at least 8 characters.',
  'error.passwordMismatch': 'The two passwords do not match.',
  'error.usernameTaken': 'That username is already in use.',
  'error.emailTaken': 'That email address is already in use.',
  'error.clientNameTaken': 'A client with that name already exists.',
  'error.productCodeTaken': 'A product with that code already exists.',
  'error.usernameFormat': 'Use 2–64 letters, digits, dots, hyphens or underscores.',
  'error.invoiceNoLines': 'An invoice needs at least one line.',
  'error.invoiceClientRequired': 'Select a client.',
  'error.quantityPositive': 'Quantity must be greater than zero.',
  'error.pricePositive': 'Price cannot be negative.',
  'error.taxRateRange': 'Tax rate must be between 0 and 100.',
  'error.unknownCurrency': 'Unknown currency.',
  'error.logoType': 'Only PNG and JPEG images are accepted.',
  'error.logoTooLarge': 'The image is larger than 2 MB.',
  'error.inUse': 'This record is referenced by other data and cannot be deleted.',

  // ── VERI*FACTU (AEAT invoice register) ────────────────────────────────────
  'nav.verifactu': 'VERI*FACTU',
  'verifactu.title': 'VERI*FACTU',
  'verifactu.subtitle': 'The AEAT invoice register. Every invoice issued is filed as a billing record, chained to the one before it.',
  'verifactu.enabled': 'File invoices with the AEAT',
  'verifactu.disabledNote': 'Off. Invoices are issued as before, with no billing record and no QR code on the PDF.',
  'verifactu.onNote': 'On. Every invoice saved from now on is filed, and a filed invoice can no longer be edited or deleted — it is withdrawn and re-issued.',

  'verifactu.connection': 'Connection',
  'verifactu.environment': 'Environment',
  'verifactu.environmentSandbox': 'Testing (AEAT homologation)',
  'verifactu.environmentProduction': 'Production — records are filed for real',
  'verifactu.environmentHint': 'Testing accepts your real certificate and returns real validation errors, but registers nothing. Start there.',
  'verifactu.endpoint': 'Endpoint',
  'verifactu.test': 'Test the connection',
  'verifactu.testing': 'Connecting…',
  'verifactu.testOk': 'AEAT accepted the certificate. It answered: {detail}',
  'verifactu.testFailed': 'AEAT could not be reached with this certificate: {detail}',
  'verifactu.testHint': 'Sends an empty request. AEAT rejecting its contents is the expected answer — it means the certificate was accepted.',

  'verifactu.certificate': 'Certificate',
  'verifactu.certificateType': 'Kind',
  'verifactu.certificateTypeCertificate': 'Personal or company certificate',
  'verifactu.certificateTypeSeal': 'Seal certificate (sello)',
  'verifactu.certificateTypeHint': 'Decides which AEAT host the records are sent to. A seal certificate uses a different one.',
  'verifactu.certificateFile': 'Certificate file',
  'verifactu.certificateFileHint': 'The .pfx or .p12 holding your AEAT certificate and its private key. Stored encrypted; never downloadable.',
  'verifactu.certificatePassword': 'Certificate password',
  'verifactu.certificatePasswordHint': 'Checked against the file before either is stored.',
  'verifactu.certificatePasswordStored': 'Stored. Leave blank to keep it, or type a new one to replace it.',
  'verifactu.certificateUpload': 'Upload certificate',
  'verifactu.certificateUploading': 'Checking…',
  'verifactu.certificateRemove': 'Remove certificate',
  'verifactu.certificateRemoveConfirm': 'Remove the stored certificate? Filing is switched off until another is uploaded. Records already filed are unaffected.',
  'verifactu.certificateNone': 'No certificate is stored.',
  'verifactu.certificateStored': 'Holder: {subject}',
  'verifactu.certificateIssuedBy': 'Issued by: {issuer}',
  'verifactu.certificateExpires': 'Expires {date}',
  'verifactu.certificateExpiresSoon': 'Expires in {days} days — submissions stop when it does.',
  'verifactu.certificateHasExpired': 'This certificate has expired. Nothing can be filed until it is replaced.',
  'verifactu.certificateUploaded': 'Certificate stored.',

  'verifactu.issuer': 'Who is filing',
  'verifactu.issuerNif': 'NIF',
  'verifactu.issuerNifHint': 'The NIF the records are filed under — nine characters. Leave blank to use the company tax ID from Company above.',
  'verifactu.issuerName': 'Name',
  'verifactu.issuerNameHint': 'Leave blank to use the company name.',
  'verifactu.effectiveIssuer': 'Records will be filed as {name} ({nif}).',

  'verifactu.numbering': 'Invoice numbering',
  'verifactu.seriesPrefix': 'Series prefix',
  'verifactu.seriesPrefixHint': 'Prefixed to the invoice number to make the series AEAT sees. The series and number together must be unique for this NIF for all time, so a prefix matters if the NIF has invoiced from another system before. Changing it later does not alter invoices already filed.',
  'verifactu.seriesExample': 'Invoice 123 would be filed as {example}.',

  'verifactu.tax': 'Tax',
  'verifactu.zeroRateCode': 'Invoices at 0% are',
  'verifactu.zeroRateCodeHint': 'What to tell AEAT about an invoice with no VAT on it. E-codes are exemptions under the article named; N-codes mean the operation is outside the scope of VAT. If you never invoice at 0%, this is never used.',
  'verifactu.zeroRateE1': 'E1 — exempt, article 20',
  'verifactu.zeroRateE2': 'E2 — exempt, article 21 (export)',
  'verifactu.zeroRateE3': 'E3 — exempt, article 22',
  'verifactu.zeroRateE4': 'E4 — exempt, articles 23 and 24',
  'verifactu.zeroRateE5': 'E5 — exempt, article 25 (intra-EU supply)',
  'verifactu.zeroRateE6': 'E6 — exempt, other',
  'verifactu.zeroRateN1': 'N1 — not subject, article 7 or 14',
  'verifactu.zeroRateN2': 'N2 — not subject, place of supply rules',
  'verifactu.regimeKey': 'VAT regime',
  'verifactu.regimeKeyHint': 'Declared on every invoice. 01 is the general regime, which is what most businesses file under.',

  'verifactu.system': 'This software',
  'verifactu.systemHint': 'Named in every record, so AEAT knows which system generated it. The defaults are correct unless you have been told otherwise.',
  'verifactu.systemName': 'System name',
  'verifactu.systemId': 'System ID',
  'verifactu.systemIdHint': 'Two characters, telling this system apart from others by the same producer.',
  'verifactu.systemVersion': 'Version',
  'verifactu.installationId': 'Installation number',
  'verifactu.producerName': 'Producer name',
  'verifactu.producerNif': 'Producer NIF',
  'verifactu.producerHint': 'Who produces the software. Leave both blank for a system you run for yourself — they then resolve to the issuer above.',

  'verifactu.autoSubmit': 'Send each record as the invoice is saved',
  'verifactu.autoSubmitHint': 'Off still generates and chains the record; it waits to be sent by hand. Useful while testing.',

  'verifactu.problems': 'This cannot be used yet',
  'verifactu.problemNoIssuerNif': 'No NIF to file under: fill in the company tax ID, or the NIF here.',
  'verifactu.problemBadIssuerNif': 'That NIF is not valid — check the characters and the final letter.',
  'verifactu.problemNoIssuerName': 'No name to file under: fill in the company name, or the name here.',
  'verifactu.problemNoCertificate': 'No certificate is stored.',
  'verifactu.problemCertificateUnreadable': 'The stored certificate cannot be read back — SESSION_SECRET has changed since it was uploaded. Upload it again.',
  'verifactu.problemCertificateExpired': 'The stored certificate has expired.',
  'verifactu.certificatePasswordWrong': 'That password does not open this certificate.',
  'verifactu.certificateUnreadable': 'That file is not a PKCS#12 certificate this can read.',
  'verifactu.seriesPrefixCharacters': 'Use only letters, digits and - _ . /',
  'verifactu.seriesNumberLength': 'The series and number together are too long for the AEAT field (60 characters).',
  'verifactu.notMigrated': 'VERI*FACTU cannot be used until the database upgrade db/11-verifactu.sql has been applied.',
  'verifactu.notEnabled': 'VERI*FACTU is switched off.',

  'verifactu.clientTaxIdMissing': 'This invoice’s client has no tax ID. AEAT identifies the recipient of an invoice, so add it to the client first.',
  'verifactu.clientTaxIdUnusable': 'This invoice’s client has a tax ID the AEAT will not accept. It needs a valid Spanish NIF, or an EU VAT number with its country prefix.',
  'verifactu.alreadyRegistered': 'This invoice is already filed, or is waiting to be.',
  'verifactu.notRegistered': 'This invoice is not on the register, so there is nothing to withdraw.',
  'verifactu.alreadyCancelled': 'This invoice has already been withdrawn.',
  'verifactu.invoiceLocked': 'This invoice is filed with the AEAT and can no longer be changed. Withdraw it and issue a corrected one.',
  'verifactu.invoiceLockedDelete': 'This invoice is filed with the AEAT and cannot be deleted. Withdraw it instead, which leaves the register honest about what happened.',

  // ── the register page ──
  'verifactu.register': 'AEAT register',
  'verifactu.registerSubtitle': 'The billing records filed for this company, newest first. Append-only: nothing here can be edited or removed.',
  'verifactu.queuePending': 'Waiting',
  'verifactu.queueAccepted': 'Filed',
  'verifactu.queueRejected': 'Rejected',
  'verifactu.queueFailed': 'Stopped',
  'verifactu.chainLength': 'Records',
  'verifactu.chainHead': 'Chain head',
  'verifactu.status.pending': 'Waiting',
  'verifactu.status.accepted': 'Filed',
  'verifactu.status.warned': 'Filed with warnings',
  'verifactu.status.rejected': 'Rejected',
  'verifactu.status.failed': 'Stopped',
  'verifactu.type.alta': 'Registration',
  'verifactu.type.anulacion': 'Withdrawal',
  'verifactu.csv': 'AEAT receipt',
  'verifactu.recordAt': 'Generated',
  'verifactu.attempts': '{count} attempts',
  'verifactu.nextAttempt': 'Next attempt {date}',
  'verifactu.send': 'Send now',
  'verifactu.sending': 'Sending…',
  'verifactu.sent': 'Record sent.',
  'verifactu.subsanacion': 'Amends a rejected record',
  'verifactu.noRecords': 'Nothing has been filed yet',
  'verifactu.noRecordsHint': 'Records appear here as invoices are issued.',
  'verifactu.filterAll': 'All records',

  // ── on an invoice ──
  'verifactu.invoiceNotFiled': 'Not filed',
  'verifactu.fileInvoice': 'File with the AEAT',
  'verifactu.filing': 'Filing…',
  'verifactu.filed': 'Invoice {number} filed with the AEAT.',
  'verifactu.withdrawInvoice': 'Withdraw from the AEAT',
  'verifactu.withdrawing': 'Withdrawing…',
  'verifactu.withdrawn': 'Invoice {number} withdrawn from the AEAT register.',
  'verifactu.withdrawConfirm': 'Withdraw invoice {number} from the AEAT register? The withdrawal is itself filed and cannot be undone — a replacement has to be issued as a new invoice.',
  'verifactu.invoiceCancelled': 'Withdrawn',
} as const;

export type TranslationKey = keyof typeof en;
export type Translations = Record<TranslationKey, string>;
